import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import { createEffect, onMount, Show } from 'solid-js';
import { useUserContext } from '../components/UserContextProvider';
import { useSFetch } from '../hooks/sfetch';
import { createSignal } from 'solid-js';
import { For } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { OAuthGrant } from '../models/OAuthGrant';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useRoleParser } from '../hooks/permissions';
import AddonState from '../components/AddonState';
import { formatDateTime } from '../services/dateFormat';
import styles from './Account.module.css';

export interface Session {
    id: string;
    info: string;
    isVerifiedAndValid: boolean;
    createdAt: string;
}

export default function Account() {

    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { parseRoles, roles } = useRoleParser();

    const sfetch = useSFetch();
    const { user, setUser } = useUserContext();
    const [sessions, setSessions] = createSignal<Session[]>([]);
    const [sessionId, setSessionId] = createSignal<string | null>(null);
    const [permissions, setPermissions] = createSignal<boolean[]>([]);

    const [oAuthGrants, setOAuthGrants] = createSignal<OAuthGrant[]>([]);

    createEffect(() => {
        if (!user().loggedin) {
            setSessions([]);
            setSessionId(null);
        }
        setPermissions(parseRoles(user().permissions ?? 0, roles));
    });

    async function loadSessions() {
        try {
            const response = await sfetch("/api/User/Sessions/me");
            setSessions(response.value);
            const id = await getSessionId();
            setSessionId(id);
        } catch (error) {
            toast?.error(t('ACCOUNT_SESSIONS_LOAD_FAILED'));
        }
    }

    async function fetchOAuthGrants() {
        try {
            const response = await sfetch("/api/user/oauth/grants");
            if (response.ok) {
                setOAuthGrants(response.value);
            } else {
                toast?.error(t('ACCOUNT_OAUTH_FETCH_FAILED'));
            }
        } catch (error) {
            toast?.error(t('ACCOUNT_OAUTH_FETCH_FAILED'));
        }
    }

    async function revokeOAuthGrant(grantId: string) {
        if (!await showConfirm(t('OAUTH_REVOKE_CONFIRM'), true)) return;
        try {
            const response = await sfetch(`/api/user/oauth/grant/${grantId}`, {
                method: "DELETE"
            });
            if (response.ok) {
                setOAuthGrants(oAuthGrants().filter(grant => grant.id !== grantId));
                toast?.info(t('ACCOUNT_OAUTH_REVOKED'));
            } else {
                toast?.error(t('ACCOUNT_OAUTH_REVOKE_FAILED'));
            }
        } catch (error) {
            toast?.error(t('ACCOUNT_OAUTH_REVOKE_FAILED'));
        }
        await fetchOAuthGrants();
    }

    onMount(async () => {
        await loadSessions();
        await fetchOAuthGrants();
    });

    async function getSessionId() {
        const res = await sfetch("/api/User/mysession", { method: "GET" });
        return res.value;
    }

    function logout() {
        sfetch("/api/User/Logout", { method: "GET" }).then(() => {
            setUser(prev => ({ ...prev, loggedin: false }));
            toast?.info(t('ACCOUNT_LOGOUT_SUCCESS'));
        }).catch(() => {
            toast?.error(t('ACCOUNT_LOGOUT_FAILED'));
        });
    }

    async function logoutSession(id: string) {
        if (!await showConfirm(t('ACCOUNT_LOGOUT_SESSION_CONFIRM'), true)) return;
        sfetch("/api/User/Session/" + id, { method: "DELETE" }).then(() => {
            setSessions(sessions().filter(session => session.id !== id));
            toast?.info(t('ACCOUNT_LOGOUT_SESSION_SUCCESS'));
        }).catch(() => {
            toast?.error(t('ACCOUNT_LOGOUT_SESSION_FAILED'));
        });
    }

    async function globallogout() {
        if (!await showConfirm(t('ACCOUNT_LOGOUT_GLOBAL_CONFIRM'), true)) return;
        sfetch("/api/User/LogoutUserGlobal/" + user().id, { method: "GET" }).then(() => {
            setUser(prev => ({ ...prev, loggedin: false }));
            toast?.info(t('ACCOUNT_LOGOUT_GLOBAL_SUCCESS'));
        }).catch(() => {
            toast?.error(t('ACCOUNT_LOGOUT_GLOBAL_FAILED'));
        });
    }

    return (
        <div class={styles.container}>
            <div class={styles.grid}>
                <div class={`fcard ${styles.profileCard}`}>
                    <h2 class={`mc ${styles.username}`}>{user().username}</h2>
                    <Show when={user()?.uuid}>
                        <img
                            class={styles.avatar}
                            src={`https://mineskin.eu/armor/body/${user().uuid}/100.png`}
                            alt={t('ACCOUNT_AVATAR_ALT')}
                        />
                    </Show>
                    <div class={styles.profileActions}>
                        <button class="btn btn-secondary" onClick={() => navigate('/account-switcher')}>
                            <i class="fa-solid fa-users" /> <Text val="SWITCH_ACCOUNT" />
                        </button>
                        <button class="btn btn-danger" onClick={logout}>
                            <i class="fa-solid fa-right-from-bracket" /> <Text val="LOGOUT" />
                        </button>
                    </div>
                    <Show when={user()?.uuid !== ""}>
                        <div class={styles.addonBlock}>
                            <AddonState id={user().uuid} />
                            <span class={styles.addonLabel}><Text val="TOTAL_ADDON_TIME" /></span>
                            <span class={styles.addonTime}>
                                {user().fifteenMinutesInAddonCount ? (user().fifteenMinutesInAddonCount! / 4) + " h" : "0 h"}
                            </span>
                        </div>
                    </Show>
                </div>

                <div class={styles.column}>
                    <div class={`fcard ${styles.section}`}>
                        <h2><Text val="PERMISSIONS" /></h2>
                        <div class={styles.permissionGrid}>
                            <For each={permissions()}>
                                {(granted, i) => (
                                    <div class={`${styles.permission} ${granted ? styles.active : ''}`}>
                                        <i class={granted
                                            ? `fa-solid fa-circle-check ${styles.permissionOn}`
                                            : `fa-solid fa-circle ${styles.permissionOff}`} />
                                        <span>{roles[i()]}</span>
                                    </div>
                                )}
                            </For>
                        </div>
                    </div>

                    <div class={`fcard ${styles.section}`}>
                        <div class={styles.sectionHeader}>
                            <h2><Text val="SESSIONS" /></h2>
                            <button class="btn btn-danger btn-sm" onClick={globallogout}>
                                <i class="fa-solid fa-power-off" /> <Text val="LOGOUTGLOBAL" />
                            </button>
                        </div>
                        <Show when={sessions().length > 0} fallback={
                            <div class={styles.empty}>
                                <i class="fa-solid fa-shield-halved" />
                                <p><Text val="NO_SESSIONS" /></p>
                            </div>
                        }>
                            <div class={styles.itemList}>
                                <For each={sessions()}>
                                    {(session) => (
                                        <div class={styles.item}>
                                            <div class={styles.itemMain}>
                                                <div class={styles.itemTitle}>
                                                    <i class={`fa-solid ${session.isVerifiedAndValid ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
                                                    {session.info}
                                                </div>
                                                <div class={styles.itemMeta}>
                                                    <span>{formatDateTime(session.createdAt)}</span>
                                                    <Show when={session.id === sessionId()}>
                                                        <span class={`${styles.badge} ${styles.badgeNeutral}`}>
                                                            <Text val="THISSESSION" />
                                                        </span>
                                                    </Show>
                                                    <Show when={session.isVerifiedAndValid} fallback={
                                                        <span class={`${styles.badge} ${styles.badgeWarn}`}><Text val="UNVERIFIED" /></span>
                                                    }>
                                                        <span class={`${styles.badge} ${styles.badgeOk}`}><Text val="VERIFIED" /></span>
                                                    </Show>
                                                </div>
                                            </div>
                                            <div class={styles.itemActions}>
                                                <button class="btn btn-danger btn-sm" onClick={() => logoutSession(session.id)} title={t('LOGOUT')}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </For>
                            </div>
                        </Show>
                    </div>

                    <div class={`fcard ${styles.section}`}>
                        <h2><Text val="OAUTH_GRANTS" /></h2>
                        <Show when={oAuthGrants().length > 0} fallback={
                            <div class={styles.empty}>
                                <i class="fa-solid fa-plug" />
                                <p><Text val="NO_OAUTH_GRANTS" /></p>
                            </div>
                        }>
                            <div class={styles.itemList}>
                                <For each={oAuthGrants()}>
                                    {(grant) => (
                                        <div class={styles.item}>
                                            <div class={styles.itemMain}>
                                                <div class={styles.itemTitle}>
                                                    <i class="fa-solid fa-plug-circle-check" />
                                                    {grant.clientName}
                                                </div>
                                                <div class={styles.itemMeta}>
                                                    <span>{grant.scope}</span>
                                                    <span>{formatDateTime(grant.createdAt)}</span>
                                                </div>
                                            </div>
                                            <div class={styles.itemActions}>
                                                <button class="btn btn-danger btn-sm" onClick={() => revokeOAuthGrant(grant.id)}>
                                                    <i class="fa-solid fa-trash" /> <Text val="REVOKE" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </For>
                            </div>
                        </Show>
                    </div>
                </div>
            </div>
        </div>
    );
}
